import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CloudX } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import HorizontalChart from '../../Charts/HorizontalChart/HorizontalChart'
import Icon from '../../Icons'
import { getCuttedString } from '../../BuildingDashboard/BuildingEssentials/helpers'
import UpsellingBlocker from '../UpsellingBlocker'
import Can from '../../Can'

import { options } from './constants'
import UnifiedInbox from './UnifiedInbox'
import FeedAndGroups from './FeedAndGroups'
import GiftCards from './GiftCards'

const MarketingData = ({ marketingData, loading, datesDescriptions }) => {
  const { t } = useTranslation(['building-analytics'])
  const announcementsLabel = t('building-analytics:sentAnnouncements.title')

  const sentAnnouncements = marketingData && {
    labels: [announcementsLabel].map((name) => getCuttedString(name, 15, 13)),
    datasets: [
      {
        label: t('building-analytics:sentAnnouncements.surveys'),
        backgroundColor: '#7978EB',
        data: [marketingData.announcements.surveys.value],
        diff: marketingData.announcements.surveys.percentageDiff,
      },
      {
        label: t('building-analytics:sentAnnouncements.sms'),
        backgroundColor: '#BB5BE4',
        data: [marketingData.announcements.sms.value],
        diff: marketingData.announcements.sms.percentageDiff,
      },
      {
        label: t('building-analytics:sentAnnouncements.pushNotifications'),
        backgroundColor: '#F1A433',
        data: [marketingData.announcements.notifications.value],
        diff: marketingData.announcements.notifications.percentageDiff,
      },
      {
        label: t('building-analytics:sentAnnouncements.email'),
        backgroundColor: '#769E8D',
        data: [marketingData.announcements.emails.value],
        diff: marketingData.announcements.emails.percentageDiff,
      },
    ].filter((item) => item.data[0]),
  }

  return (
    <div className="marketing-data">
      <div className="marketing-data__header">
        <div className="title-with-icon">
          <Icon styles={{ width: '20px', height: '20px' }} viewBox="0 0 24 24" icon="marketing" />
          <h3>{t('building-analytics:marketingData.title')}</h3>
        </div>
        <p>
          {t('building-analytics:common.dateRangeCompared', {
            start: datesDescriptions[0],
            end: datesDescriptions[1],
          })}
        </p>
      </div>
      {loading ? (
        <div className="loading">
          <h3>....</h3>
          <h3>{t('building-analytics:common.loading')}</h3>
        </div>
      ) : (
        <div className="marketing-data__main-block">
          <div className="marketing-data__main-block__subblock">
            <div className="marketing-data__main-block__subblock__header">
              <h3 className="reservations-title">{announcementsLabel}</h3>
              <Link className="redirect-button" to="/dashboard/announcements">
                <span>{t('building-analytics:sentAnnouncements.goToAnnouncements')}</span>
                <Icon
                  styles={{ width: '20px', paddingLeft: '5px' }}
                  viewBox="0 0 20 20"
                  icon={'arrowRight'}
                  svgProps={{
                    stroke: '#646464',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                  fill="none"
                />
              </Link>
            </div>

            <Can
              on={'Navbar'}
              to={'communicate'}
              feedFlag={'communicationAvailable'}
              upsellingLink={
                <UpsellingBlocker
                  type="communicate"
                  text={t('building-analytics:sentAnnouncements.upsellingText')}
                  wrapperClassName="marketing-data__main-block__subblock__upselling-blocker"
                />
              }
            >
              <div className="marketing-data__main-block__subblock__announcements">
                {marketingData && sentAnnouncements.datasets.length ? (
                  <div className="announcement-wrapper">
                    <h3 className="title">
                      {t('building-analytics:sentAnnouncements.announcementSent')}
                    </h3>
                    <div className="stack-bar-wrapper">
                      <HorizontalChart
                        withPlugins
                        data={sentAnnouncements}
                        options={options}
                        height={sentAnnouncements.datasets.length * 18}
                        wrapperStyles={{ width: '954px' }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="no-data">
                    <CloudX size={24} />
                    <h3>{t('building-analytics:common.noDataTitle')}</h3>
                    <p>{t('building-analytics:common.noDataDescription')}</p>
                  </div>
                )}
              </div>
            </Can>
          </div>
          <div className="marketing-data__three-blocks__wrapper">
            <UnifiedInbox marketingData={marketingData} />
            <FeedAndGroups marketingData={marketingData} />
            <GiftCards marketingData={marketingData} />
          </div>
        </div>
      )}
    </div>
  )
}

MarketingData.propTypes = {
  marketingData: PropTypes.object,
  loading: PropTypes.bool,
  datesDescriptions: PropTypes.array,
}

export default MarketingData
